import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Logo from './Logo';
import NavIcon from './NavIcon';
import { APP_NAME, APP_VERSION, BUILD_VERSION } from '../version';
import { useAuth } from '../context/AuthContext';
import { useBusiness } from '../context/BusinessContext';

// Standalone links shown above the groups.
const TOP = [
  { to: '/', label: 'Dashboard', end: true, gate: 'dashboard.view', icon: 'gauge' },
  { to: '/board', label: 'My Board', end: true, icon: 'filecheck' },
];

// Day-to-day work, grouped into collapsible sub-navs.
const GROUPS = [
  { label: 'Sales', icon: 'trending', items: [
    { to: '/invoices', label: 'Invoices', gate: 'invoices.view', icon: 'file' },
    { to: '/quotes', label: 'Quotes', gate: 'quotes.view', icon: 'file' },
    { to: '/customers', label: 'Customers', gate: 'customers.view', icon: 'user' },
    { to: '/statements', label: 'Statements', gate: 'invoices.view', icon: 'file' },
    { to: '/items', label: 'Items', gate: 'items.view', icon: 'box' },
    { to: '/contracts', label: 'Contracts', gate: 'contracts.view', icon: 'filecheck' },
    { to: '/agreements', label: 'Agreements', gate: 'contracts.view', icon: 'file' },
  ] },
  { label: 'Banking', icon: 'bank', items: [
    { to: '/payments', label: 'Payments', gate: 'payments.view', icon: 'card' },
    { to: '/deposits', label: 'Deposits', gate: 'deposits.view', icon: 'deposit' },
    { to: '/reconcile', label: 'Reconcile', gate: 'reconciliations.view', icon: 'check' },
  ] },
  { label: 'Purchases', icon: 'cart', items: [
    { to: '/expenses', label: 'Expenses', gate: 'expenses.view', icon: 'receipt' },
    { to: '/bills', label: 'Bills', gate: 'expenses.view', icon: 'receipt' },
    { to: '/approvals', label: 'Approvals', gate: 'expenses.view', icon: 'filecheck' },
  ] },
];

// Standalone links shown below the groups.
const BOTTOM = [
  // SIM360 (S360) doesn't use Jobs; Not My Airplane (B3) calls them Flights.
  { to: '/jobs', label: 'Jobs', gate: 'jobs.view', icon: 'plane', hideForShort: ['S360'], labelByShort: { B3: 'Flights' } },
  { to: '/service-reports', label: 'Service Reports', gate: 'service_reports.view', icon: 'wrench' },
  { to: '/reports', label: 'Reports', gate: 'reports.view', icon: 'chart' },
];

function linkClass({ isActive }) {
  return 'nav-item' + (isActive ? ' nav-item--active' : '');
}

export default function Sidebar() {
  const { can } = useAuth();
  const { activeBusiness } = useBusiness();
  const { pathname } = useLocation();
  const short = activeBusiness?.short;

  const visible = (items) => items
    .filter((i) => can(i.gate) && !(i.hideForShort && i.hideForShort.includes(short)))
    .map((i) => ({ ...i, label: (i.labelByShort && i.labelByShort[short]) || i.label }));

  const top = visible(TOP);
  const bottom = visible(BOTTOM);
  const groups = GROUPS
    .map((g) => ({ ...g, items: visible(g.items) }))
    .filter((g) => g.items.length > 0);

  // Collapsed state per group; a group containing the current route starts open.
  const [collapsed, setCollapsed] = useState({});
  const toggle = (label) => setCollapsed((c) => ({ ...c, [label]: !c[label] }));

  const renderLink = (item) => (
    <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
      <NavIcon name={item.icon} />
      <span className="nav-item__label">{item.label}</span>
    </NavLink>
  );

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <Logo />
        <span className="sidebar__wordmark">{APP_NAME}</span>
      </div>

      <nav className="sidebar__nav" aria-label="Primary">
        {top.map(renderLink)}

        {groups.map((g) => {
          const hasActive = g.items.some((i) => pathname === i.to);
          const open = hasActive || !collapsed[g.label];
          return (
            <div className="nav-group" key={g.label}>
              <button type="button" className="nav-group__head" onClick={() => toggle(g.label)} aria-expanded={open}>
                <span className="nav-group__title"><NavIcon name={g.icon} size={16} /> {g.label}</span>
                <span className={'nav-group__caret' + (open ? ' is-open' : '')} aria-hidden="true">▸</span>
              </button>
              {open && <div className="nav-group__items">{g.items.map(renderLink)}</div>}
            </div>
          );
        })}

        {bottom.map(renderLink)}
      </nav>

      <div className="sidebar__foot">
        <span className="build-chip" title={`Build ${BUILD_VERSION}`}>
          v{APP_VERSION} · {BUILD_VERSION}
        </span>
      </div>
    </aside>
  );
}
